import * as React from "react";
import { Database } from "lucide-react";

import {
  SOURCE_CLIENT_LABELS,
  SourceCard,
  extractSourceItemsFromPools,
  type Citation,
  type SourceItem,
} from "../citations";
import { shouldUseFactSetBridge } from "../services/presentationHost";
import type { ChatMessage } from "../types";
import { isTaskGeneratedMessage } from "../utils";
import { SourceGroupSection } from "./SourceGroupSection";

interface SlideCitationsViewProps {
  /**
   * All chat messages from the active slide's session. The view derives
   * its content from these — every assistant turn that produced
   * citations contributes its sources to the aggregated list.
   *
   * Deck-creation slides participate in this same flow via a synthetic
   * assistant message pushed into the slide's tab session by
   * `useDeckCreation` — the message carries per-slide `citations` and
   * `citedValuesByUuid`. The view is intentionally
   * agnostic to whether a message came from a chat edit or from
   * deck-from-task generation.
   */
  messages: ChatMessage[];
  /**
   * Click handler for the "View source" button on each card. Receives
   * the message-scoped citation pool (so the drawer's `findCitationById`
   * / cross-reference lookups still work) and the full source item.
   */
  onSourceView?: (citations: Citation[], item: SourceItem) => void;
  onCitationView?: (citations: Citation[], id: string) => void;
}

/**
 * Inline view rendered behind the "Citations" tab on the chat toolbar.
 * Aggregates every source emitted across the slide's chat history,
 * de-duplicates per *document* (Dictionary citations) or per *table*
 * (Markdown citations), and groups by data source.
 *
 * Why per-document and not per-uuid? Citation envelopes are organized
 * as `{ url -> link }` for SEC/IR/Web and as a single markdown table
 * for FactSet. Showing one card per inline `<u id>` uuid produces
 * dozens of duplicate cards pointing at the same 10-K filing — see
 * `sourceItems.ts` for the full design rationale.
 *
 * This intentionally does NOT read from FactSet slide tags — the
 * source of truth for the "Citations" toggle is the in-session chat
 * history per the product spec. The persisted slide-tag-backed view
 * lives in `SlideCitationsPanel` and is reachable from the side nav.
 */
export const SlideCitationsView: React.FC<SlideCitationsViewProps> = ({
  messages,
  onSourceView,
  onCitationView,
}) => {
  // Flatten every assistant turn's citation pool into a per-document /
  // per-table source-item list (deduplicated across turns). Also fold
  // every turn's cited-value map together so the cards can surface the
  // displayed value next to each source, AND so we can build the
  // quoted-uuid filter (only show source documents the model actually
  // cited; hide retrieved-but-not-cited Finster results).
  //
  // `hasAnyCitedMap` distinguishes "no turn extracted cited values"
  // (legacy chat history saved before model-only extraction landed —
  // we should NOT filter, otherwise old turns would render empty)
  // from "every turn extracted but the model cited nothing" (we
  // should filter to nothing — render the empty state).
  const { items, pool, citedValues } = React.useMemo(() => {
    const pools: Array<Citation[] | undefined> = [];
    const mergedPool: Citation[] = [];
    const mergedCited: Record<string, string> = {};
    let hasAnyCitedMap = false;
    for (const msg of messages) {
      if (msg.role !== "assistant") continue;
      // Intentionally read ONLY the per-slide `citations`/`citedValuesByUuid`.
      // `retrievedCitations`/`retrievedCitedValuesByUuid` are turn-level
      // retrieved sources shown in the chat bubble where the chat happened;
      // they are deliberately excluded here so a chat on the source slide does
      // not populate the source slide's Citations tab with values placed on
      // other slides.
      const cits = msg.citations as Citation[] | undefined;
      if (Array.isArray(cits) && cits.length > 0) {
        pools.push(cits);
        for (const c of cits) {
          if (c) mergedPool.push(c);
        }
      }
      const cv = (msg.citedValuesByUuid ??
        (isTaskGeneratedMessage(msg) ? {} : undefined)) as
        | Record<string, string>
        | undefined;
      if (cv) {
        hasAnyCitedMap = true;
        const citedIds = Object.keys(cv);
        for (const id of citedIds) {
          if (!(id in mergedCited)) mergedCited[id] = cv[id];
        }
      }
    }
    const quotedUuids = hasAnyCitedMap
      ? new Set(Object.keys(mergedCited))
      : undefined;
    const citedItems = extractSourceItemsFromPools(pools, {
      clientLabels: SOURCE_CLIENT_LABELS,
      quotedUuids,
    });
    return { items: citedItems, pool: mergedPool, citedValues: mergedCited };
  }, [messages]);

  // Group by the source item's resolved label, preserving first-seen
  // order. Keep the grouping inline so we don't take a dependency on
  // `groupItemsByDataSource` (which expects a different item shape).
  const groups = React.useMemo(() => {
    const order: string[] = [];
    const buckets: Record<string, SourceItem[]> = {};
    for (const item of items) {
      const label = item.label || "Other";
      if (!(label in buckets)) {
        buckets[label] = [];
        order.push(label);
      }
      buckets[label].push(item);
    }
    return order.map((label) => ({ label, items: buckets[label] }));
  }, [items]);

  if (items.length === 0) {
    return (
      <div className="flex h-full flex-1 flex-col items-center justify-center gap-2 px-6 py-10 text-center">
        <Database className="h-6 w-6 text-muted-foreground" aria-hidden />
        <p className="text-[13px] font-medium text-foreground">
          No cited sources for this slide yet
        </p>
        <p className="max-w-[260px] text-[12px] leading-[1.5] text-muted-foreground">
          Sources show up here only when the assistant actually quotes a
          value from FactSet, SEC filings, web search, etc. Documents{" "}
          {shouldUseFactSetBridge() ? "FactSet AI" : "Finster"} pulled as
          background context but never cited stay hidden.
        </p>
      </div>
    );
  }

  return (
    <div className="flex w-full min-w-0 flex-col gap-1">
      {groups.map((group) => (
        <SourceGroupSection key={group.label} label={group.label} items={group.items}>
          {group.items.map((item) => (
            <SourceCard
              key={item.id}
              item={item}
              citedValuesByUuid={citedValues}
              onView={(target) => onSourceView?.(pool, target)}
              onCitationView={(id) => onCitationView?.(pool, id)}
            />
          ))}
        </SourceGroupSection>
      ))}
    </div>
  );
};

